/**
 * Step 1 of the CDMX-20060 template chain: a note's raw `(note type, note description)` -> its
 * CMX note type, through the health system's "Note Type" dictionary in dictionaries-service.
 *
 * A PORT, not a design. case-automate resolves the same value into the `description_cmx` case
 * fact with `BpmFunctionMapper.findBestMatchingCMXNoteType`, and the viewer must land on the
 * label automation would have landed on for the same dictionary, or a coder sees one note type on
 * screen and is scored against another. Every rule below is that method's, including the ones
 * that look accidental:
 *
 *   - a missing cell, or one whose value is blank, MATCHES anything;
 *   - `*` matches anything, and a cell holding `*` still counts as specified;
 *   - comparison ignores case;
 *   - the row specifying the most columns wins, a present cell counting as specified even when
 *     its value is blank -- only a null value does not count;
 *   - on a tie the EARLIER row wins (strict `<`);
 *   - a row with no `CMX Note Type` cell is skipped;
 *   - nothing matching is `"Unrecognized Note Type"`.
 *
 * The test suite mirrors `BpmFunctionMapperTest`'s cases one for one, by name.
 *
 * Note type CODES are not reliable on their own -- one health system sends none at all, and codes
 * fan out across several note types -- which is why the description column carries the lookup.
 * Document metadata keeps both raw values; this resolves at view time, against today's dictionary.
 * A case resolved its `description_cmx` when it ran, so the two can differ if the dictionary was
 * edited in between. That is intended: the viewer shows the current mapping.
 */

export const NOTE_TYPE_DICTIONARY_NAME = 'Note Type';
export const NOTE_TYPE_COLUMN = 'Note Type';
export const NOTE_DESCRIPTION_COLUMN = 'Note Description';
export const CMX_NOTE_TYPE_COLUMN = 'CMX Note Type';
export const UNRECOGNIZED_NOTE_TYPE = 'Unrecognized Note Type';

/**
 * One cell of a dictionaries-service `RowDto`, as `GET /dictionary/{name}/row/v2` serialises it.
 * The column is named on both `columnDefinition.name` (what Java's `RowDto.findValue` reads) and
 * `columnName`; either is accepted.
 */
export type DictionaryCell = {
  columnName?: string | null;
  columnDefinition?: { name?: string | null } | null;
  value?: string | null;
};

export type DictionaryRow = {
  cells?: DictionaryCell[] | null;
};

function findCell(
  row: DictionaryRow,
  columnName: string
): DictionaryCell | undefined {
  return (row.cells ?? []).find(
    (cell) => (cell.columnDefinition?.name ?? cell.columnName) === columnName
  );
}

/** `StringUtils.isBlank`. */
function isBlank(value: string | null | undefined): boolean {
  return value == null || value.trim().length === 0;
}

/** `BpmFunctionMapper.isMatch`. */
function isMatch(
  cell: DictionaryCell | undefined,
  value: string | null | undefined
): boolean {
  if (!cell || isBlank(cell.value)) {
    return true;
  }
  const cellValue = cell.value as string;
  return (
    (value != null && cellValue.toLowerCase() === value.toLowerCase()) ||
    cellValue === '*'
  );
}

/** `BpmFunctionMapper.findBestMatchingCMXNoteType`. */
export function resolveCmxNoteType(
  rows: readonly DictionaryRow[],
  noteType: string | null | undefined,
  noteDescription: string | null | undefined
): string {
  let cmxNoteType: string | null = UNRECOGNIZED_NOTE_TYPE;
  let maxCount = 0;

  for (const row of rows) {
    const typeCell = findCell(row, NOTE_TYPE_COLUMN);
    const descriptionCell = findCell(row, NOTE_DESCRIPTION_COLUMN);
    const cmxCell = findCell(row, CMX_NOTE_TYPE_COLUMN);

    if (!cmxCell) {
      continue;
    }

    const specifiesType = typeCell?.value != null;
    const specifiesDescription = descriptionCell?.value != null;
    const columnsSpecified =
      (specifiesType ? 1 : 0) + (specifiesDescription ? 1 : 0);

    if (
      isMatch(typeCell, noteType) &&
      isMatch(descriptionCell, noteDescription) &&
      maxCount < columnsSpecified
    ) {
      cmxNoteType = cmxCell.value ?? null;
      maxCount = columnsSpecified;
    }
  }

  // Java returns the cell's null as-is; both mean "no template", and a string is easier to log.
  return cmxNoteType ?? UNRECOGNIZED_NOTE_TYPE;
}
